import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  InnocodeButton,
  InnocodeDatatable,
  InnocodeDropdown,
  InnocodeInput,
  useDialog,
  useInnocodeLoadingOverlay,
} from "@intsoft/innocode-core";
import { useTenantList } from "../../../utils/service/tenantList";
import { ModalContext } from "../../../utils/modal/ModalProvider";
import DetailModal from "../../../utils/modal/DetailModal";
import { ssoAdminApi } from "../api";
import styles from "./ServiceGroups.module.css";
import ServiceGroupFormModal from "./modal/ServiceGroupFormModal";
import ServiceGroupDetailModal from "./modal/ServiceGroupDetailModal";
import ServiceGroupServiceManageModal from "./modal/ServiceGroupServiceManageModal";

const PAGE_SIZE = 10;
const STATUS_ALL = "ALL";

const statusOptions = [
  { value: STATUS_ALL, label: "전체 상태" },
  { value: "ACTIVE", label: "활성" },
  { value: "INACTIVE", label: "비활성" },
  { value: "DELETED", label: "삭제" },
];

const extractItems = (result) => {
  if (Array.isArray(result?.content)) {
    return result.content;
  }

  if (Array.isArray(result?.items)) {
    return result.items;
  }

  if (Array.isArray(result?.data)) {
    return result.data;
  }

  if (Array.isArray(result)) {
    return result;
  }

  return [];
};

const toSafeNumber = (...values) => {
  for (const value of values) {
    const numberValue = Number(value);
    if (Number.isFinite(numberValue)) {
      return numberValue;
    }
  }

  return null;
};

const formatGroupTypeLabel = (groupType) => {
  if (String(groupType || "").trim().toUpperCase() === "FEDERATION") {
    return "연합형";
  }

  return "내부형";
};

const formatStatusLabel = (status) => {
  if (status === "ACTIVE") {
    return "활성";
  }

  if (status === "INACTIVE") {
    return "비활성";
  }

  if (status === "DELETED") {
    return "삭제";
  }

  return status || "-";
};

const getStatusClassName = (status) => {
  if (status === "ACTIVE") {
    return `${styles.statusText} ${styles.statusActive}`;
  }

  if (status === "INACTIVE") {
    return `${styles.statusText} ${styles.statusInactive}`;
  }

  if (status === "DELETED") {
    return `${styles.statusText} ${styles.statusDeleted}`;
  }

  return styles.statusText;
};

const formatRelationTypeLabel = (relationType) => {
  if (relationType === "OWNER") {
    return "오너";
  }

  if (relationType === "PARTICIPANT") {
    return "파트너";
  }

  return relationType || "-";
};

const normalizeRelationType = (group) => {
  const relationType = String(
    group?.relationType || group?.relation || group?.myRelation || group?.role || ""
  )
    .trim()
    .toUpperCase();

  if (relationType === "OWNER") {
    return "OWNER";
  }

  if (relationType === "PARTICIPANT") {
    return "PARTICIPANT";
  }

  if (typeof group?.ownedByCurrentTenant === "boolean") {
    return group.ownedByCurrentTenant ? "OWNER" : "PARTICIPANT";
  }

  return "";
};

const buildServiceSummary = (row) => {
  const serviceCount = toSafeNumber(
    row?.serviceCount,
    row?.memberCount,
    row?.membersCount,
    row?.registeredServiceCount,
    Array.isArray(row?.members) ? row.members.length : null
  );
  const participantTenantCount = toSafeNumber(
    row?.participatingTenantCount,
    row?.participantTenantCount,
    row?.joinedTenantCount,
    row?.tenantCount
  );
  const description = String(
    row?.serviceSummary || row?.description || row?.groupDescription || ""
  ).trim();

  let primary = "등록 서비스 정보 없음";
  if (serviceCount != null) {
    primary = `등록 서비스 ${serviceCount}건`;
  } else if (description) {
    primary = description;
  }

  const meta = [];

  if (participantTenantCount != null) {
    meta.push(`참여 테넌트 ${participantTenantCount}곳`);
  }

  if (serviceCount != null && description) {
    meta.push(description);
  }

  return {
    primary,
    meta: meta.length > 0 ? meta.join(" · ") : "-",
  };
};

const normalizeRelationRow = (group, tenantMetaMap) => {
  const ownerTenantId = group?.ownerTenantId || group?.ownerTenant || "";
  const contextTenantId = group?.contextTenantId || group?.tenantId || "";
  const relationType = normalizeRelationType(group);
  const ownerTenantName =
    group?.ownerTenantName ||
    group?.ownerTenantLabel ||
    tenantMetaMap[ownerTenantId]?.tenantName ||
    tenantMetaMap[ownerTenantId]?.name ||
    ownerTenantId ||
    "-";
  const contextTenantName =
    group?.contextTenantName ||
    group?.contextTenantLabel ||
    tenantMetaMap[contextTenantId]?.tenantName ||
    tenantMetaMap[contextTenantId]?.name ||
    contextTenantId ||
    "-";

  return {
    ...group,
    rowKey: [
      group?.groupId || group?.id || "",
      contextTenantId,
      relationType,
    ].join(":"),
    groupId: group?.groupId || group?.id || "",
    groupKey: group?.groupKey || "",
    groupName: group?.groupName || "",
    ownerTenantId,
    ownerTenantName,
    contextTenantId,
    contextTenantName,
    relationType,
    groupType: group?.groupType || "INTERNAL",
    status: group?.status || "ACTIVE",
    serviceSummary: buildServiceSummary(group),
  };
};

function ServiceGroupsPage() {
  const { tenantList } = useTenantList();
  const { openModal, closeModal } = useContext(ModalContext);
  const { showDialog } = useDialog();
  const { show: showLoading, hide: hideLoading } = useInnocodeLoadingOverlay();
  const [relationRows, setRelationRows] = useState([]);
  const [status, setStatus] = useState(STATUS_ALL);
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(0);

  const tenantMetaMap = useMemo(
    () =>
      tenantList.reduce((acc, tenant) => {
        acc[tenant.tenantId] = tenant;
        return acc;
      }, {}),
    [tenantList]
  );

  const tenantOptions = useMemo(
    () =>
      tenantList.map((tenant) => ({
        value: tenant.tenantId,
        label: tenant.tenantName || tenant.tenantId,
      })),
    [tenantList]
  );

  const accessibleTenantIds = useMemo(
    () => new Set(tenantList.map((tenant) => tenant.tenantId)),
    [tenantList]
  );

  const loadRelationRows = useCallback(async () => {
    showLoading();

    try {
      const result = await ssoAdminApi.fetchServiceGroups({
        ...(status !== STATUS_ALL ? { status } : {}),
        page: 0,
        size: 500,
      });

      setRelationRows(
        extractItems(result).map((group) => normalizeRelationRow(group, tenantMetaMap))
      );
    } catch (error) {
      setRelationRows([]);
      showDialog({
        iconType: "warning",
        type: "error",
        title: "목록 조회 실패",
        message:
          error?.response?.data?.message ||
          "서비스 그룹 관계 목록을 불러오지 못했습니다.",
        confirmText: "확인",
      });
    } finally {
      hideLoading();
    }
  }, [hideLoading, showDialog, showLoading, status, tenantMetaMap]);

  useEffect(() => {
    loadRelationRows();
  }, [loadRelationRows]);

  useEffect(() => {
    setPage(0);
  }, [keyword, status]);

  const filteredRows = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase();

    return relationRows.filter((row) => {
      if (!normalizedKeyword) {
        return true;
      }

      return [
        row.groupName,
        row.groupKey,
        row.ownerTenantName,
        row.contextTenantName,
        row.relationType,
        row.serviceSummary.primary,
        row.serviceSummary.meta,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(normalizedKeyword)
        );
    });
  }, [keyword, relationRows]);

  const pagedRows = useMemo(() => {
    const startIndex = page * PAGE_SIZE;
    return filteredRows.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredRows, page]);

  const summary = useMemo(() => {
    const ownerCount = relationRows.filter(
      (row) => row.relationType === "OWNER"
    ).length;
    const participantCount = relationRows.filter(
      (row) => row.relationType === "PARTICIPANT"
    ).length;
    const activeCount = relationRows.filter((row) => row.status === "ACTIVE").length;

    return {
      total: relationRows.length,
      ownerCount,
      participantCount,
      activeCount,
    };
  }, [relationRows]);

  const openCreateModal = useCallback(() => {
    const modalContent = (
      <ServiceGroupFormModal
        tenantOptions={tenantOptions}
        defaultOwnerTenantId={tenantOptions[0]?.value || ""}
        onClose={closeModal}
        onSaved={loadRelationRows}
      />
    );

    openModal(
      <DetailModal
        content={modalContent}
        option={{ modalTitle: "서비스 그룹 생성" }}
      />
    );
  }, [closeModal, loadRelationRows, openModal, tenantOptions]);

  const openDetailModal = useCallback(
    (row) => {
      const modalContent = (
        <ServiceGroupDetailModal
          groupId={row.groupId}
          contextTenantId={row.contextTenantId}
          contextTenantName={row.contextTenantName}
          relationType={row.relationType}
          tenantOptions={tenantOptions}
          onClose={closeModal}
          onSaved={loadRelationRows}
        />
      );

      openModal(
        <DetailModal
          content={modalContent}
          option={{ modalTitle: "서비스 그룹 상세" }}
          className={styles.detailModalWidth}
        />
      );
    },
    [closeModal, loadRelationRows, openModal, tenantOptions]
  );

  const openServiceManageModal = useCallback(
    (row) => {
      const modalContent = (
        <ServiceGroupServiceManageModal
          groupId={row.groupId}
          contextTenantId={row.contextTenantId}
          contextTenantName={row.contextTenantName}
          relationType={row.relationType}
          onClose={closeModal}
          onSaved={loadRelationRows}
        />
      );

      openModal(
        <DetailModal
          content={modalContent}
          option={{ modalTitle: "서비스 관리" }}
          className={styles.serviceManageModalWidth}
        />
      );
    },
    [closeModal, loadRelationRows, openModal]
  );

  const columns = useMemo(
    () => [
      {
        id: "groupName",
        header: "서비스 그룹",
        accessorKey: "groupName",
        enableSorting: false,
        cell: ({ row }) => (
          <div className={styles.groupNameCell}>
            <div className={styles.groupName}>{row.groupName || "-"}</div>
            <div className={styles.groupKey}>{row.groupKey || row.groupId || "-"}</div>
          </div>
        ),
      },
      {
        id: "ownerTenant",
        header: "소유 테넌트",
        accessorKey: "ownerTenantName",
        enableSorting: false,
        cell: ({ row }) => (
          <div className={styles.tenantCell}>
            <div className={styles.groupName}>{row.ownerTenantName || "-"}</div>
          </div>
        ),
      },
      {
        id: "contextTenant",
        header: "내 테넌트",
        accessorKey: "contextTenantName",
        enableSorting: false,
        cell: ({ row }) => (
          <div className={styles.tenantCell}>
            <div className={styles.groupName}>{row.contextTenantName || "-"}</div>
          </div>
        ),
      },
      {
        id: "relationType",
        header: "관계",
        accessorKey: "relationType",
        enableSorting: false,
        cell: ({ row }) => (
          <span className={styles.relationBadge}>
            {formatRelationTypeLabel(row.relationType)}
          </span>
        ),
      },
      {
        id: "groupType",
        header: "유형",
        accessorKey: "groupType",
        enableSorting: false,
        cell: ({ row }) => <span>{formatGroupTypeLabel(row.groupType)}</span>,
      },
      {
        id: "status",
        header: "상태",
        accessorKey: "status",
        enableSorting: false,
        cell: ({ row }) => (
          <span className={getStatusClassName(row.status)}>
            {formatStatusLabel(row.status)}
          </span>
        ),
      },
      {
        id: "serviceSummary",
        header: "서비스 현황",
        accessorKey: "serviceSummary",
        enableSorting: false,
        cell: ({ row }) => (
          <div className={styles.groupNameCell}>
            <div className={styles.groupName}>{row.serviceSummary.primary}</div>
            {/* <div className={styles.groupKey}>{row.serviceSummary.meta}</div> */}
          </div>
        ),
      },
      {
        id: "action",
        header: "관리",
        accessorKey: "action",
        enableSorting: false,
        cell: ({ row }) => (
          <div className={styles.inlineButtonRow}>
            <InnocodeButton
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                openServiceManageModal(row);
              }}
            >
              서비스 관리
            </InnocodeButton>
          </div>
        ),
      },
    ],
    [openDetailModal, openServiceManageModal]
  );

  return (
    <div className={styles.pageWrapper}>
      <div>
        <h2 className={styles.pageTitle}>서비스 그룹 관리</h2>
      </div>

      <div className={styles.contentBox}>
        <div className={styles.summaryGrid}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>전체 관계</div>
            <div className={styles.summaryValue}>{summary.total}</div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>오너</div>
            <div className={styles.summaryValue}>{summary.ownerCount}</div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>파트너</div>
            <div className={styles.summaryValue}>{summary.participantCount}</div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>활성화</div>
            <div className={styles.summaryValue}>{summary.activeCount}</div>
          </div>
        </div>

        <div className={styles.tableSection}>
          <InnocodeDatatable
            className={styles.dataTable}
            data={pagedRows}
            columns={columns}
            onRowClick={openDetailModal}
            enableCustomHeader={true}
            customHeader={
              <div className={styles.customHeaderContainer}>
                <div className={styles.customHeaderSearchContainer}>
                  <InnocodeInput
                    className={styles.searchInput}
                    value={keyword}
                    placeholder="서비스 그룹 검색"
                    onChange={(event) => setKeyword(event.target.value)}
                  />
                  <div className={styles.dropdownWrapper}>
                    <InnocodeDropdown
                      value={status}
                      options={statusOptions}
                      onChange={(value) => setStatus(value || STATUS_ALL)}
                    />
                  </div>
                </div>
                <div className={styles.customHeaderActionContainer}>
                  <InnocodeButton onClick={openCreateModal}>그룹 생성</InnocodeButton>
                </div>
              </div>
            }
            enableTableHeader={true}
            enableSorting={false}
            enableCheckbox={false}
            enablePagination={true}
            pageSize={PAGE_SIZE}
            totalCount={filteredRows.length}
            currentPage={page}
            onPageChange={setPage}
            emptyMessage="표시할 서비스 그룹 관계 행이 없습니다."
          />
        </div>
      </div>
    </div>
  );
}

export default ServiceGroupsPage;
